/**
 * Environment configuration loaded from Vite environment variables
 */

export interface PollingConfig {
    active: number;
    normal: number;
    idle: number;
    veryIdle: number;
    idleThreshold: number;
    veryIdleThreshold: number;
}

/**
 * Welcome messages configuration with language keys
 */
export interface WelcomeMessages {
    da: string;
    en: string;
}

export interface EnvConfig {
    serverUrl: string;
    agentId: string;
    agentName: string;
    language: 'da' | 'en';
    enableLogging: boolean;
    layoutDebug: boolean;
    initialMode: 'minimized' | 'popup' | 'fullscreen';
    autoStartSession: boolean;
    showAttribution: boolean;
    pollingConfig?: PollingConfig;
    welcomeMessages: WelcomeMessages;
    /** Default token ID ('john', 'jane') or custom JWT */
    defaultToken?: string;
    /** Hide session history sidebar in fullscreen mode (default: false) */
    hideSessionSidebar: boolean;
    /** Hide all chat UI (Configure Chat, modal, ChatBot) - show landing page only (default: false) */
    hideChatUi: boolean;
}

/**
 * Parses a boolean string from environment variables
 * @param value - The string value to parse
 * @param defaultValue - The default value if parsing fails
 * @returns The parsed boolean value
 */
const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
    if (!value) return defaultValue;
    return value.toLowerCase() === 'true';
};

/**
 * Parses a number string from environment variables
 * @param value - The string value to parse
 * @param defaultValue - The default value if parsing fails
 * @returns The parsed number value
 */
const parseNumber = (value: string | undefined, defaultValue: number): number => {
    if (!value) return defaultValue;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Default welcome messages used when not configured via environment
 */
const DEFAULT_WELCOME_MESSAGES: WelcomeMessages = {
    da: 'Hej {{customerName}} - velkommen til {{agentName}}!\n\nHvordan kan jeg hjælpe dig i dag? Du er velkommen til at spørge om hvad som helst!',
    en: 'Hello {{customerName}} - welcome to {{agentName}}!\n\nHow can I help you today? Feel free to ask me anything!'
};

/**
 * Parses welcome messages from a JSON string in environment variables
 * @param value - The JSON string value to parse
 * @returns WelcomeMessages object with da and en keys
 */
const parseWelcomeMessages = (value: string | undefined): WelcomeMessages => {
    if (!value) return DEFAULT_WELCOME_MESSAGES;
    
    try {
        const parsed = JSON.parse(value);
        // Validate that both keys exist and are strings
        if (typeof parsed.da === 'string' && typeof parsed.en === 'string') {
            return {
                da: parsed.da,
                en: parsed.en
            };
        }
        console.warn('VITE_WELCOME_MESSAGES must have "da" and "en" string properties. Using defaults.');
        return DEFAULT_WELCOME_MESSAGES;
    } catch (error) {
        console.warn('Failed to parse VITE_WELCOME_MESSAGES as JSON. Using defaults.', error);
        return DEFAULT_WELCOME_MESSAGES;
    }
};

/**
 * Gets the polling configuration from environment variables
 * @returns PollingConfig object or undefined if not configured
 */
const getPollingConfig = (): PollingConfig | undefined => {
    const env = import.meta.env || {};

    // If no polling config is set, return undefined to use defaults
    if (!env.VITE_POLLING_ACTIVE && !env.VITE_POLLING_NORMAL) {
        return undefined;
    }

    return {
        active: parseNumber(env.VITE_POLLING_ACTIVE, 500),
        normal: parseNumber(env.VITE_POLLING_NORMAL, 1000),
        idle: parseNumber(env.VITE_POLLING_IDLE, 3000),
        veryIdle: parseNumber(env.VITE_POLLING_VERY_IDLE, 5000),
        idleThreshold: parseNumber(env.VITE_POLLING_IDLE_THRESHOLD, 10000),
        veryIdleThreshold: parseNumber(env.VITE_POLLING_VERY_IDLE_THRESHOLD, 30000),
    };
};

/**
 * Loads and validates environment configuration
 * @returns EnvConfig object with all configuration values
 */
export const getEnvConfig = (): EnvConfig => {
    const env = import.meta.env || {};

    return {
        serverUrl: env.VITE_SERVER_URL || 'http://localhost:8800',
        agentId: env.VITE_AGENT_ID || '',
        agentName: env.VITE_AGENT_NAME || '',
        language: (env.VITE_LANGUAGE as 'da' | 'en') || 'da',
        enableLogging: parseBoolean(env.VITE_ENABLE_LOGGING, false),
        layoutDebug: parseBoolean(env.VITE_LAYOUT_DEBUG, false),
        initialMode: (env.VITE_INITIAL_MODE as 'minimized' | 'popup' | 'fullscreen') || 'popup',
        autoStartSession: parseBoolean(env.VITE_AUTO_START_SESSION, true),
        showAttribution: parseBoolean(env.VITE_SHOW_ATTRIBUTION, false),
        pollingConfig: getPollingConfig(),
        welcomeMessages: parseWelcomeMessages(env.VITE_WELCOME_MESSAGES),
        defaultToken: env.VITE_DEFAULT_TOKEN || undefined,
        hideSessionSidebar: parseBoolean(env.VITE_HIDE_SESSION_SIDEBAR, false),
        hideChatUi: parseBoolean(env.VITE_HIDE_CHAT_UI, false),
    };
};

/**
 * Validates that required environment variables are set
 * @throws Error if required variables are missing
 */
export const validateEnvConfig = (): void => {
    const env = import.meta.env || {};

    const missing: string[] = [];

    if (!env.VITE_SERVER_URL) {
        missing.push('VITE_SERVER_URL');
    }

    if (!env.VITE_AGENT_ID) {
        missing.push('VITE_AGENT_ID');
    }

    if (missing.length > 0) {
        console.warn(
            `Missing required environment variables: ${missing.join(', ')}\n` +
            'Please copy .env.example to .env and configure the required values.'
        );
    }
};

